<script setup lang="ts">
import { useTemplateRef } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import FaceSelectionCard from './FaceSelectionCard.vue';
import type {
  FaceDemographicField,
  FaceSelectionFace,
} from './faceSelectionTypes';

interface Props {
  faces: readonly FaceSelectionFace[];
  getDropdownLabel: (
    face: FaceSelectionFace,
    field: FaceDemographicField
  ) => string;
  isScrollableLeftArrowVisible: boolean;
  isScrollableRightArrowVisible: boolean;
  selectedFaceUuids: readonly string[];
  selectionFull?: boolean;
  showAgeSelector: boolean;
  showGenderSelector: boolean;
  showRaceSelector: boolean;
}

interface Emits {
  arrowClick: [delta: number];
  dropdownOpen: [
    face: FaceSelectionFace,
    field: FaceDemographicField,
    event: MouseEvent,
  ];
  faceClick: [face: FaceSelectionFace];
  scroll: [event: Event];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const scrollable = useTemplateRef<HTMLDivElement>('scrollable');

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function isFaceSelected(face: FaceSelectionFace): boolean {
  return props.selectedFaceUuids.includes(face.uuid);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function forwardDropdownOpen(
  face: FaceSelectionFace,
  field: FaceDemographicField,
  event: MouseEvent
): void {
  emit('dropdownOpen', face, field, event);
}

function getScrollable(): HTMLDivElement | null {
  return scrollable.value;
}

defineExpose({ getScrollable });
</script>

<template>
  <div class="FacesIndividual__select">
    <div
      class="FacesIndividual__select-arrow FacesIndividual__select-arrow--left"
      :class="{
        'FacesIndividual__select-arrow--visible':
          isScrollableLeftArrowVisible,
      }"
      @click="emit('arrowClick', -1)"
    >
      <i
        class="FacesIndividual__select-side-border FacesIndividual__select-side-border--start"
      />
      <div class="FacesIndividual__select-arrow-inner">
        <i class="FacesIndividual__select-arrow-icon" />
      </div>
    </div>

    <div
      ref="scrollable"
      class="FacesIndividual__select-scrollable hidden-scrollbar"
      data-testid="faces-scrollable"
      @scroll="emit('scroll', $event)"
    >
      <ul class="FacesIndividual__select-list">
        <li
          class="FacesIndividual__select-side-padding FacesIndividual__select-side-padding--left"
        />
        <FaceSelectionCard
          v-for="face of faces"
          :key="face.uuid"
          :face="face"
          :get-dropdown-label="getDropdownLabel"
          :selected="isFaceSelected(face)"
          :unavailable="selectionFull && !isFaceSelected(face)"
          :show-age-selector="showAgeSelector"
          :show-gender-selector="showGenderSelector"
          :show-race-selector="showRaceSelector"
          @dropdown-open="forwardDropdownOpen"
          @face-click="emit('faceClick', $event)"
        />
        <li
          class="FacesIndividual__select-side-padding FacesIndividual__select-side-padding--right"
        />
      </ul>
    </div>

    <div
      class="FacesIndividual__select-arrow FacesIndividual__select-arrow--right"
      :class="{
        'FacesIndividual__select-arrow--visible':
          isScrollableRightArrowVisible,
      }"
      @click="emit('arrowClick', 1)"
    >
      <div class="FacesIndividual__select-arrow-inner">
        <i class="FacesIndividual__select-arrow-icon" />
      </div>
    </div>

    <i
      class="FacesIndividual__select-side-border FacesIndividual__select-side-border--end"
    />
  </div>
</template>

<style lang="scss" scoped>
.FacesIndividual__select {
  position: relative;
}

.FacesIndividual__select-scrollable {
  width: var(--FacesIndividual__select-scrollable-width);
  max-width: var(--FacesIndividual__select-scrollable-max-width);
  height: 100%;
  overflow-x: var(--FacesIndividual__select-scrollable-overflow-x);
  overflow-y: var(--FacesIndividual__select-scrollable-overflow-y);
  padding: var(--FacesIndividual__select-scrollable-padding);
  scroll-behavior: smooth;
}

.FacesIndividual__select-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: var(--FacesIndividual__select-list-display);
  grid-template-columns: var(--FacesIndividual__select-list-grid-template-columns);
  gap: var(--FacesIndividual__select-list-gap);
  flex-direction: var(--FacesIndividual__select-list-flex-direction);
  flex-wrap: wrap;
  align-items: var(--FacesIndividual__select-list-align-items);
  justify-content: var(--FacesIndividual__select-list-justify-content);
  height: var(--FacesIndividual__select-list-rendered-height);
  max-height: var(--FacesIndividual__select-list-max-height);
  max-width: var(--FacesIndividual__select-list-max-width);
  margin-left: var(--FacesIndividual__select-list-margin-left);
}

.FacesIndividual__select-arrow {
  display: var(--FacesIndividual__select-arrow-display);
  position: absolute;
  top: 0;
  width: var(--FacesIndividual__select-arrow-width);
  height: var(--FacesIndividual__select-list-height);
  z-index: 1;
  cursor: pointer;
  user-select: none;
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms;

  &--left {
    left: 0;

    .FacesIndividual__select-arrow-icon {
      transform: translate(-75%, -50%) rotate(180deg);
    }
  }

  &--right {
    right: 0;
  }

  &--visible {
    opacity: 1;
    pointer-events: all;
  }
}

.FacesIndividual__select-arrow-inner {
  position: absolute;
  inset: 0;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 5%;
    left: 0;
    width: 130%;
    height: 90%;
    background: #000;
    filter: blur(1.5rem);
    border-radius: 100%;
    opacity: 0.75;
    transition: opacity 200ms;

    .FacesIndividual__select-arrow:hover & {
      opacity: 1;
    }

    .FacesIndividual__select-arrow--left & {
      transform: translateX(-75%);
    }

    .FacesIndividual__select-arrow--right & {
      transform: translateX(55%);
    }
  }
}

.FacesIndividual__select-side-border {
  display: none;
}

.FacesIndividual__select-arrow-icon {
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;
  width: calc(var(--FacesIndividual__select-arrow-width) * 0.5);
  height: calc(var(--FacesIndividual__select-arrow-width) * 0.5);
  transform: translate(-25%, -50%);
  background: {
    image: url('../../../assets/images/gallery-arrow.svg');
    size: contain;
    position: center;
    repeat: no-repeat;
  };
}

.FacesIndividual__select-side-padding {
  display: var(--FacesIndividual__select-side-padding-display);
  width: calc(var(--FacesIndividual__select-section-gap) - (var(--FacesIndividual__select-item-gap) / 2) + 0.3rem);
  height: var(--FacesIndividual__select-list-height);
}
</style>
